import { useEffect, useState } from "react";
import { useAppSelector } from "../../../App/hooks";
import {
  deleteMarketmakingPredefinedProgram,
  getMarketmakingPredefinedPrograms,
  getMarketmakingProgram,
  setMarketmakingPredefinedProgram,
  setMarketmakingProgram,
} from "../../../Requests/marketmaking";
import {
  IMarketingProgramRowItem,
  IMarketmakingProgram,
  IMarketmakingPredefinedProgram,
} from "../../../Interfaces/marketmaking";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import { toast } from "react-toastify";
import Table from "../../../Components/Table";
import { TableCell, TableRow } from "@mui/material";
import TextInput from "../../../Components/TextInput";
import Button from "../../../Components/Button";
import Modal from "../../../Components/Modal/Modal";
import DeleteIcon from "@mui/icons-material/Delete";
import StaticDropdown from "../../../Components/Dropdown/StaticDropdown";

type ProgramType = "buy" | "sell";

const Program = () => {
  const taskTableClickedRowValues = useAppSelector(
    (store) => store.marketmaking.clickedTaskTableRowValues
  );

  const [program, setProgram] = useState({} as IMarketmakingProgram);
  const [isPredefinedModalOpen, setIsPredefinedModalOpen] = useState(false);
  const [predefinedProgramName, setPredefinedProgramName] = useState("");
  const [predefinedPrograms, setPredefinedPrograms] = useState(
    [] as IMarketmakingPredefinedProgram[]
  );
  const [selectedPredefinedProgram, setSelectedPredefinedProgram] =
    useState("");
  const [predefinedProgramsRefreshFlag, setPredefinedProgramsRefreshFlag] =
    useState(true);

  useEffect(() => {
    if (!predefinedProgramsRefreshFlag) return;
    getMarketmakingPredefinedPrograms()
      .then((resp) => {
        setPredefinedPrograms(resp);
      })
      .catch(packetErrorHandler)
      .finally(() => setPredefinedProgramsRefreshFlag(false));
  }, [predefinedProgramsRefreshFlag]);

  useEffect(() => {
    if (!taskTableClickedRowValues?.id) return;
    getMarketmakingProgram(taskTableClickedRowValues.id)
      .then((resp) => {
        const buy = Array.from(resp.buy);
        const sell = Array.from(resp.sell);

        for (let index = 0; index < 5 - resp.buy.length; index++) {
          buy.push({
            flexibilityAmount: "" as any,
            flexibilityPercent: "" as any,
            from: "",
            // no: res.sell.length + j,
            to: "",
          } as IMarketingProgramRowItem);
        }
        for (let index = 0; index < 5 - resp.sell.length; index++) {
          sell.push({
            flexibilityAmount: "" as any,
            flexibilityPercent: "" as any,
            from: "",
            // no: res.sell.length + j,
            to: "",
          } as IMarketingProgramRowItem);
        }
        setProgram({ buy, sell });
      })
      .catch(packetErrorHandler);
  }, [taskTableClickedRowValues?.id]);

  const setProgramHandler = () => {
    if (!taskTableClickedRowValues) return;
    setMarketmakingProgram({
      taskId: taskTableClickedRowValues.id,
      buy: program.buy.filter(
        (b) => b.flexibilityAmount || b.flexibilityPercent || b.from || b.to
      ),
      sell: program.sell.filter(
        (s) => s.flexibilityAmount || s.flexibilityPercent || s.from || s.to
      ),
    })
      .then((resp) => {
        if (resp.data) toast.success(resp.msg);
        else toast.error(resp.msg);
      })
      .catch(packetErrorHandler);
  };

  const setPredefinedProgramHandler = () => {
    if (!taskTableClickedRowValues) return;
    setMarketmakingPredefinedProgram({
      buy: program.buy.filter(
        (b) => b.flexibilityAmount || b.flexibilityPercent || b.from || b.to
      ),
      sell: program.sell.filter(
        (s) => s.flexibilityAmount || s.flexibilityPercent || s.from || s.to
      ),
      programName: predefinedProgramName,
    })
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          setIsPredefinedModalOpen(false);
          setPredefinedProgramsRefreshFlag(true);
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler);
  };

  const changeHandler = (
    name: string,
    value: any,
    index: number,
    type: ProgramType
  ) => {
    const clone = { ...program };

    clone[type].splice(index, 1, { ...clone[type][index], [name]: value });
    setProgram(clone);
  };

  const header = (type: ProgramType) => (
    <>
      <TableCell>ردیف</TableCell>
      <TableCell>حجم {type === "buy" ? "خرید" : "فروش"} ابتدای بازه</TableCell>
      <TableCell>حجم {type === "buy" ? "خرید" : "فروش"} انتهای بازه</TableCell>
      <TableCell>
        قیمت {type === "buy" ? "خرید" : "فروش"} مقدار انعطاف
      </TableCell>
      <TableCell>قیمت {type === "buy" ? "خرید" : "فروش"} درصد انعطاف</TableCell>
    </>
  );

  const body = (type: ProgramType) => (
    <>
      {program && program.buy ? (
        program[type].map((row, i) => (
          <TableRow>
            <TableCell>{i + 1}</TableCell>
            <TableCell>
              <TextInput
                label=""
                name="from"
                onChange={(...params) => changeHandler(...params, i, type)}
                value={program[type][i].from}
              />
            </TableCell>
            <TableCell>
              <TextInput
                label=""
                name="to"
                onChange={(...params) => changeHandler(...params, i, type)}
                value={program[type][i].to}
              />
            </TableCell>
            <TableCell>
              <TextInput
                label=""
                name="flexibilityAmount"
                onChange={(...params) => changeHandler(...params, i, type)}
                value={program[type][i].flexibilityAmount?.toString()}
              />
            </TableCell>
            <TableCell>
              <TextInput
                label=""
                name="flexibilityPercent"
                onChange={(...params) => changeHandler(...params, i, type)}
                value={program[type][i].flexibilityPercent?.toString()}
              />
            </TableCell>
          </TableRow>
        ))
      ) : (
        <></>
      )}
    </>
  );

  const getTable = (type: ProgramType) => (
    <div className="w-min m-auto border border-sky-100 rounded overflow-hidden order-table">
      <Table header={header(type)} body={body(type)} />
    </div>
  );

  const checkDisabled = () => {
    if (!program) return true;

    const buy = program.buy?.filter(
      (b) => b.flexibilityAmount || b.flexibilityPercent || b.from || b.to
    );
    const sell = program.sell?.filter(
      (s) => s.flexibilityAmount || s.flexibilityPercent || s.from || s.to
    );
    const filteredBuy = buy?.filter(
      (b) => b?.flexibilityAmount && b?.flexibilityPercent && b?.from && b?.to
    );
    const filteredSell = sell?.filter(
      (s) => s?.flexibilityAmount && s?.flexibilityPercent && s?.from && s?.to
    );

    if (!filteredBuy?.length || !filteredSell.length) return true;

    if (
      filteredBuy?.length === buy?.length &&
      filteredSell?.length === sell?.length
    )
      return false;
    return true;
  };

  const predefinedProgramModalUI = (
    <div className="w-full flex flex-col justify-start items-start gap-5">
      <div className="w-44">
        <TextInput
          label="نام پیشفرض"
          name=""
          onChange={(_, v) => setPredefinedProgramName(v)}
          value={predefinedProgramName}
        />
      </div>
      <div className="flex flex-row justify-between items-start w-full gap-5">
        {getTable("buy")}
        {getTable("sell")}
      </div>
      <Button
        text="ثبت"
        variant="contained"
        onClick={setPredefinedProgramHandler}
        disabled={checkDisabled()}
      />
    </div>
  );

  const deletePredefinedProgramHandler = (name?: string) => {
    if (!name) return;
    deleteMarketmakingPredefinedProgram(name)
      .then((resp) => {
        if (!resp.data) toast.error(resp.msg);
        else {
          toast.success(resp.msg);
          setPredefinedProgramsRefreshFlag(true);
          setSelectedPredefinedProgram("");
        }
      })
      .catch(packetErrorHandler);
  };

  const onPredefinedChange = (name: string) => {
    setSelectedPredefinedProgram(name);
    const target =
      predefinedPrograms.find((p) => p.programName === name) ||
      ({} as IMarketmakingPredefinedProgram);
    const buy = Array.from(target.buy);
    const sell = Array.from(target.sell);

    for (let index = 0; index < 5 - target.buy.length; index++) {
      buy.push({
        flexibilityAmount: "" as any,
        flexibilityPercent: "" as any,
        from: "",
        // no: res.sell.length + j,
        to: "",
      } as IMarketingProgramRowItem);
    }
    for (let index = 0; index < 5 - target.sell.length; index++) {
      sell.push({
        flexibilityAmount: "" as any,
        flexibilityPercent: "" as any,
        from: "",
        // no: res.sell.length + j,
        to: "",
      } as IMarketingProgramRowItem);
    }
    setProgram({ buy, sell });
  };

  return (
    <div className="flex flex-col justify-start items-start gap-3 w-full">
      {isPredefinedModalOpen ? (
        <Modal title="برنامه پیش‌فرض"
          UI={predefinedProgramModalUI}
          onClose={() => {
            setIsPredefinedModalOpen(false);
            setPredefinedProgramName("");
          }}
        />
      ) : (
        <></>
      )}
      <div className="flex flex-row justify-between items-start w-full">
        {getTable("buy")}
        {getTable("sell")}
      </div>
      <div className="flex flex-row justify-start items-center gap-2 mr-auto">
        <div className="flex flex-row justify-start items-center gap-3">
          {selectedPredefinedProgram ? (
            <div
              onClick={() =>
                deletePredefinedProgramHandler(selectedPredefinedProgram)
              }
              className="mr-auto"
            >
              <DeleteIcon
                fontSize="small"
                className="text-red-700 cursor-pointer"
              />
            </div>
          ) : (
            <></>
          )}
          <StaticDropdown
            dropdown={{ name: "", placeholder: "پیش فرض" }}
            options={predefinedPrograms.map((p) => ({
              id: p.programName,
              name: p.programName,
            }))}
            onChange={(_, v) => onPredefinedChange(v)}
            value={selectedPredefinedProgram}
          />
        </div>
        <Button
          text="ثبت پیشفرض"
          variant="outlined"
          onClick={() => setIsPredefinedModalOpen(true)}
          disabled={checkDisabled()}
        />
        <Button
          text="ثبت"
          variant="contained"
          onClick={setProgramHandler}
          disabled={checkDisabled()}
        />
      </div>
    </div>
  );
};

export default Program;
